import React, { useState } from "react";
import {
  traduzirErroTemplate,
  ICONE_CATEGORIA,
  ErroTemplateInfo,
} from "./templateErrors";

// Bloco visual reutilizável para exibir um erro de template "traduzido".
// Usado em /chat (dentro do popover de falha) e em /templates-com-erro (faixa de erro).
//
// Mostra sempre: ícone da categoria + título amigável + resumo.
// Botão ⓘ ("Por que falhou?"): revela causa provável + o que fazer.
// Link "Ver mensagem original do WhatsApp": revela o texto cru retornado pela API.
//
// Sem fundo/borda próprios — herda o container (balão do chat ou faixa vermelha do painel).
const TemplateErrorDetails: React.FC<{
  motivo?: string | null;
  isDark?: boolean;
  /** Abre a seção de causa/ação já expandida (padrão: fechado). */
  detalhesAbertos?: boolean;
  /** Tamanho da fonte base (px). Padrão 12.5. */
  fontSize?: number;
}> = ({ motivo, isDark, detalhesAbertos = false, fontSize = 12.5 }) => {
  const [abertoCausa, setAbertoCausa] = useState(detalhesAbertos);
  const [abertoOriginal, setAbertoOriginal] = useState(false);

  const info: ErroTemplateInfo = traduzirErroTemplate(motivo);
  const original = (motivo || "").trim();
  const corLink = isDark ? "#fca5a5" : "#b91c1c";
  const corMuted = isDark ? "#cbb6b6" : "#9b6b6b";

  const linkBtn: React.CSSProperties = {
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    color: corLink,
    fontWeight: 600,
    fontSize: fontSize - 1.5,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    textAlign: "left",
  };

  return (
    <div style={{ fontSize, lineHeight: 1.45 }}>
      {/* Título + ícone da categoria */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontWeight: 700,
          marginBottom: 3,
        }}
      >
        <i className={ICONE_CATEGORIA[info.categoria]} style={{ fontSize: fontSize + 1 }} />
        <span>{info.titulo}</span>
      </div>

      {/* Resumo (sempre visível) */}
      <div style={{ marginBottom: 6 }}>{info.resumo}</div>

      {/* Botão ⓘ — revela causa provável + o que fazer */}
      <button
        type="button"
        style={linkBtn}
        onClick={() => setAbertoCausa((v) => !v)}
        aria-expanded={abertoCausa}
      >
        <i className="ti ti-info-circle" style={{ fontSize: fontSize + 1 }} />
        {abertoCausa ? "Ocultar detalhes" : "Por que falhou?"}
      </button>

      {abertoCausa && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 6,
            borderTop: `1px dashed ${isDark ? "#6b3a3a" : "#f0c4c4"}`,
          }}
        >
          <div style={{ marginBottom: info.acao ? 6 : 0 }}>
            <strong>Causa provável:</strong> {info.causa}
          </div>
          {info.acao && (
            <div>
              <strong>O que fazer:</strong> {info.acao}
            </div>
          )}
        </div>
      )}

      {/* Mensagem original do WhatsApp (recolhida) */}
      {original && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            style={{ ...linkBtn, color: corMuted, fontWeight: 500 }}
            onClick={() => setAbertoOriginal((v) => !v)}
            aria-expanded={abertoOriginal}
          >
            <i
              className={abertoOriginal ? "ti ti-chevron-down" : "ti ti-chevron-right"}
              style={{ fontSize: fontSize }}
            />
            Ver mensagem original do WhatsApp
          </button>
          {abertoOriginal && (
            <div
              style={{
                marginTop: 4,
                fontStyle: "italic",
                opacity: 0.85,
                fontSize: fontSize - 1.5,
                wordBreak: "break-word",
              }}
            >
              {original}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TemplateErrorDetails;
